import React, { useEffect, useMemo, useState } from "react";
import { Country, State } from "country-state-city";
import { MapPin, Globe, Check, Plus, X } from "lucide-react";

/**
 * Global geography picker for the Supplier Listing form (iter31).
 *
 * Value shape:
 *  { global: bool, countries: [{code, name, states: [ISO], nationwide: bool}] }
 *
 * Backwards-compatibility: also emits flat legacy fields so the backend + Quick-Filter
 * aggregations that were built around country_code / state_codes / nationwide keep working.
 */
export default function ServiceRegionsField({ value, onChange }) {
  const [search, setSearch] = useState("");
  const [addingCountry, setAddingCountry] = useState("");
  const [expandedCode, setExpandedCode] = useState("");
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const isGlobal = !!value.global;
  const countries = value.countries || [];

  const setGlobal = (on) => {
    if (on) {
      onChange({
        ...value,
        global: true,
        countries: [],
        // legacy mirror
        country_name: "",
        country_code: "",
        state_codes: [],
        nationwide: false,
      });
    } else {
      onChange({ ...value, global: false });
    }
    setExpandedCode("");
  };

  const emitLegacyMirror = (next) => {
    // First country becomes the legacy primary country for backwards-compat filters
    const primary = next.countries[0];
    return {
      ...next,
      country_name: primary?.name || "",
      country_code: primary?.code || "",
      state_codes: primary?.states || [],
      nationwide: !!primary?.nationwide,
    };
  };

  const addCountry = (iso) => {
    if (!iso || countries.some((c) => c.code === iso)) return;
    const country = allCountries.find((c) => c.isoCode === iso);
    if (!country) return;
    const next = {
      ...value,
      global: false,
      countries: [...countries, { code: country.isoCode, name: country.name, states: [], nationwide: false }],
    };
    onChange(emitLegacyMirror(next));
    setAddingCountry("");
    setExpandedCode(country.isoCode);
    setSearch("");
  };

  const removeCountry = (iso) => {
    const next = { ...value, countries: countries.filter((c) => c.code !== iso) };
    onChange(emitLegacyMirror(next));
    if (expandedCode === iso) setExpandedCode("");
  };

  const patchCountry = (iso, patch) => {
    const next = {
      ...value,
      countries: countries.map((c) => (c.code === iso ? { ...c, ...patch } : c)),
    };
    onChange(emitLegacyMirror(next));
  };

  const toggleNationwide = (iso) => {
    const c = countries.find((x) => x.code === iso);
    if (!c) return;
    if (c.nationwide) {
      patchCountry(iso, { nationwide: false, states: [] });
    } else {
      const allStates = State.getStatesOfCountry(iso).map((s) => s.isoCode);
      patchCountry(iso, { nationwide: true, states: allStates });
    }
  };

  const toggleState = (iso, stateIso) => {
    const c = countries.find((x) => x.code === iso);
    if (!c) return;
    const cur = new Set(c.states || []);
    if (cur.has(stateIso)) cur.delete(stateIso);
    else cur.add(stateIso);
    const nextStates = Array.from(cur);
    patchCountry(iso, { states: nextStates, nationwide: nextStates.length > 0 && nextStates.length === State.getStatesOfCountry(iso).length });
  };

  const clearAll = () => {
    onChange({ ...value, global: false, countries: [], country_name: "", country_code: "", state_codes: [], nationwide: false });
    setExpandedCode("");
  };

  const searchableCountries = allCountries.filter((c) => !countries.some((x) => x.code === c.isoCode));

  return (
    <div className="col-span-2 border border-[#E8ECE9] bg-[#F9F8F5] p-4 rounded-sm" data-testid="service-regions-field">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-[#11211A] flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#264839]" /> Service Regions
          </div>
          <div className="text-[11px] text-[#7D8C85] mt-0.5">
            Where do you supply? Buyers filter by country + state on every category page.
          </div>
        </div>
        {(isGlobal || countries.length > 0) && (
          <button type="button" onClick={clearAll} className="text-[11px] text-[#B35C4A] hover:underline" data-testid="regions-clear">Clear</button>
        )}
      </div>

      {/* Supply Globally checkbox — hides everything below when on */}
      <label className="flex items-center gap-2 mb-3 cursor-pointer select-none" data-testid="regions-global-block">
        <input
          type="checkbox"
          checked={isGlobal}
          onChange={(e) => setGlobal(e.target.checked)}
          className="w-4 h-4 accent-[#264839]"
          data-testid="regions-supply-globally"
        />
        <span className="text-sm text-[#11211A] font-medium flex items-center gap-1.5">
          <Globe className="w-4 h-4 text-[#264839]" /> Supply globally
        </span>
        <span className="text-[11px] text-[#7D8C85]">— tick if you ship worldwide, no country restrictions.</span>
      </label>

      {isGlobal ? (
        <div className="text-[11px] text-[#264839] italic pt-1" data-testid="regions-global-active">
          Your listing will surface to buyers in every country. Individual country/state selection is disabled while Global is on.
        </div>
      ) : (
        <>
          {/* Selected countries + their nested state pickers */}
          {countries.length > 0 && (
            <div className="space-y-3 mb-3" data-testid="regions-countries-list">
              {countries.map((c) => {
                const availableStates = State.getStatesOfCountry(c.code);
                const filteredStates = search.trim()
                  ? availableStates.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()) || (s.isoCode || "").toLowerCase().includes(search.toLowerCase()))
                  : availableStates;
                const isExpanded = expandedCode === c.code;
                const flag = allCountries.find((x) => x.isoCode === c.code)?.flag || "";
                return (
                  <div key={c.code} className="bg-white border border-[#E8ECE9] rounded-sm" data-testid={`regions-country-card-${c.code}`}>
                    <div className="px-3 py-2 flex items-center gap-2">
                      <span className="text-lg">{flag}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#11211A]">{c.name}</div>
                        <div className="text-[10px] text-[#7D8C85]">
                          {c.nationwide
                            ? `Nationwide · all ${availableStates.length} states`
                            : (c.states || []).length > 0
                              ? `${c.states.length} state${c.states.length === 1 ? "" : "s"} selected`
                              : availableStates.length === 0
                                ? "No ISO subdivisions — nationwide assumed"
                                : "No states selected yet"}
                        </div>
                      </div>
                      {availableStates.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpandedCode(isExpanded ? "" : c.code)}
                          className="text-[11px] px-2 py-1 border border-[#C4CFC9] hover:border-[#264839] hover:text-[#264839]"
                          data-testid={`regions-country-expand-${c.code}`}
                        >
                          {isExpanded ? "Done" : "Pick states"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeCountry(c.code)}
                        className="text-[#7D8C85] hover:text-red-600 p-1"
                        data-testid={`regions-country-remove-${c.code}`}
                        aria-label={`Remove ${c.name}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {isExpanded && availableStates.length > 0 && (
                      <div className="border-t border-[#E8ECE9] p-3">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          {/* All States / Nationwide chip at the TOP of the list */}
                          <button
                            type="button"
                            onClick={() => toggleNationwide(c.code)}
                            className={`text-[11px] px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${c.nationwide ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#4A5550] border-[#264839] font-semibold"}`}
                            data-testid={`regions-all-states-${c.code}`}
                          >
                            {c.nationwide && <Check className="w-3 h-3" />}
                            <Globe className="w-3 h-3" /> All States / Nationwide
                          </button>
                          {availableStates.length > 10 && (
                            <input
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search states…"
                              className="text-xs px-2 py-1 border border-[#C4CFC9] rounded-sm w-32"
                              data-testid={`regions-search-${c.code}`}
                            />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 bg-[#F9F8F5] border border-[#E8ECE9] rounded-sm">
                          {filteredStates.map((s) => {
                            const on = (c.states || []).includes(s.isoCode);
                            return (
                              <button
                                key={s.isoCode}
                                type="button"
                                onClick={() => toggleState(c.code, s.isoCode)}
                                className={`text-[11px] px-2 py-1 border transition inline-flex items-center gap-1 ${on ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839]"}`}
                                data-testid={`region-state-${c.code}-${s.isoCode}`}
                              >
                                {on && <Check className="w-3 h-3" />}
                                {s.isoCode} · {s.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add-another-country dropdown */}
          <div className="flex items-center gap-2" data-testid="regions-add-country-block">
            <div className="text-xs text-[#4A5550] flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> {countries.length === 0 ? "Add your first country" : "Add another country"}
            </div>
            <select
              value={addingCountry}
              onChange={(e) => addCountry(e.target.value)}
              className="text-sm px-3 py-1.5 border border-[#C4CFC9] bg-white rounded-sm flex-1"
              data-testid="regions-add-country-select"
            >
              <option value="">— Select country —</option>
              {searchableCountries.map((c) => (
                <option key={c.isoCode} value={c.isoCode}>{c.flag ? `${c.flag} ` : ""}{c.name}</option>
              ))}
            </select>
          </div>
          {countries.length === 0 && (
            <p className="text-[11px] text-[#7D8C85] mt-2 italic">Pick at least one country — or tick <em>Supply globally</em> above.</p>
          )}
        </>
      )}
    </div>
  );
}
